import React from "react";
import { Paper, Stack, Typography } from "@mui/material";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import _ from "lodash";
import { actuatorLabel, describeLoopRange, formatLoopValue, rangeFromThresholds } from "app/shared/automations/controlLoopDisplay";
import { SOURCE_LABELS, clock, stateLabel } from "app/shared/devices/actuatorDisplay";
import SummaryChip from "../../components/SummaryChip";

// Chips de la tarjeta de un control en la lista: salida, rango, límites que la protegen y estado actual.
export const ControlLoopCardSummary = ({ item, compact = false }) => {
    const loop = item.data || {};
    const actuator = loop.actuator || {};
    const { min, max } = rangeFromThresholds(loop);
    const limits = actuator.safety_limits || [];

    return (
        <>
            {!compact && <SummaryChip icon={<PowerSettingsNewOutlinedIcon />} label={actuator.name || "Salida"} color="warning" />}
            <SummaryChip icon={<TuneOutlinedIcon />} label={`${formatLoopValue(min, loop.measurement_unit)} – ${formatLoopValue(max, loop.measurement_unit)}`} color="success" />
            {!!limits.length && (
                <SummaryChip
                    icon={<ShieldOutlinedIcon />}
                    label={compact ? `${limits.length}` : `${limits.length} límite${limits.length === 1 ? "" : "s"}`}
                    color={_.some(limits, "tripped") ? "error" : "default"}
                />
            )}
            <SummaryChip
                label={stateLabel(actuator.effective_state)}
                color={actuator.effective_state === "ON" ? "success" : "default"}
                variant={actuator.effective_state === "ON" ? "filled" : "outlined"}
            />
        </>
    );
};

// La salida está en manual, protegida por un límite cruzado o con el equipo bloqueado: se ve sin abrir la tarjeta.
export const controlLoopStatusChip = (item) => {
    const actuator = item.data?.actuator || {};
    if (actuator.locked_out) return { label: "Equipo bloqueado", color: "error" };
    const tripped = _.find(actuator.safety_limits, "tripped");
    if (tripped) return { label: `Límite «${tripped.name}» cruzado`, color: "error" };
    if (actuator.manual_hold_until) return { label: `Manual hasta las ${clock(actuator.manual_hold_until)}`, color: "warning" };
    return null;
};

const DetailBox = ({ title, children }) => (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, flex: 1, bgcolor: "rgba(15, 23, 42, 0.02)" }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>{title}</Typography>
        <Stack spacing={0.4} sx={{ mt: 0.75 }}>{children}</Stack>
    </Paper>
);

export const ControlLoopCardDetails = ({ item }) => {
    const loop = item.data || {};
    const actuator = loop.actuator || {};
    const limits = actuator.safety_limits || [];

    return (
        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} sx={{ pt: 1.5 }}>
            <DetailBox title="Mantiene">
                <Typography variant="body2">{loop.measurement_label} de <b>{loop.sensor?.name || loop.sensor_name}</b></Typography>
                <Typography variant="body2">{describeLoopRange(loop)}</Typography>
                {loop.last_reading_at && (
                    <Typography variant="caption" color="text.secondary">Última lectura usada: {clock(loop.last_reading_at)}</Typography>
                )}
            </DetailBox>
            <DetailBox title="Con">
                <Typography variant="body2">
                    <b>{actuatorLabel(actuator)}</b>: {stateLabel(actuator.effective_state)}
                    {actuator.desired_source && ` por ${SOURCE_LABELS[actuator.desired_source] || actuator.desired_source}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {_.compact([
                        `Si el sensor no informa en ${loop.lease_minutes} min, queda ${actuator.fail_safe_state === "ON" ? "encendida" : "apagada"}`,
                        loop.max_on_hours && `máximo ${loop.max_on_hours} h encendida`,
                    ]).join(" · ")}
                </Typography>
            </DetailBox>
            <DetailBox title="Protegida por">
                {limits.length
                    ? limits.map((limit) => (
                        <Typography key={`limit-${limit.id}`} variant="body2" color={limit.tripped ? "error.main" : "text.primary"}>
                            {limit.name}{limit.tripped ? " · cruzado" : ""}{limit.enabled ? "" : " · pausado"}
                        </Typography>
                    ))
                    : <Typography variant="body2" color="text.secondary">Ningún límite de seguridad</Typography>}
            </DetailBox>
        </Stack>
    );
};

export const controlLoopSubtitle = (item) => {
    const loop = item.data || {};
    return item.description || `${loop.measurement_label || loop.measurement} de ${loop.sensor?.name || loop.sensor_name || "su sensor"} · ${_.lowerFirst(describeLoopRange(loop))}`;
};
